import { getGlobalEventsLogs } from '@cian/events-log';
import { getWebview } from '@cian/webview/webview';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setPushNotificationsAllowed } from '../../actions/page';
import { trackPushSettingsPopupClick } from '../../analytics';
import { RequestPushSettings } from '../../components/RequestPushSettings';
import { selectIsWebview, selectPushSettingsModalOpened } from '../../selectors/page';
import { useApplicationContext } from '../../utils/applicationContext';
import { PUSH_TOPIC, PUSH_SKIP, PUSH_ALLOW } from '../ValuationSubmitContainer/utils/requestPushSettingsModal';

export const RequestPushSettingsContainer: React.VFC = () => {
  const isModalOpened = useSelector(selectPushSettingsModalOpened);
  const isWebview = useSelector(selectIsWebview);
  const { logger } = useApplicationContext();
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!isWebview) {
      throw new Error('RequestPushSettingsContainer is webview-only component');
    }

    const webview = getWebview();

    /**
     * Экшн запрашивает у аппки состояние пуш-уведомлений (разрешены/выключены).
     * Аппка при получении этого экшна вызовет функцию sendNotificationPermissionsResult, передав результат
     * */
    webview.sendAction({ action: 'requestPushNotificationStatus' });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenSettingsClick = React.useCallback(() => {
    const webview = getWebview();

    trackPushSettingsPopupClick(true);

    /** Экшн для открытия экрана системных настроек пуш-уведомлений Cian */
    webview.sendAction({ action: 'requestNotificationPermissions' });
  }, []);

  const handleSkipSettingsClick = React.useCallback(() => {
    const globalLog = getGlobalEventsLogs();
    globalLog.produce(PUSH_TOPIC, { type: PUSH_SKIP, value: true });

    trackPushSettingsPopupClick(false);
  }, []);

  const sendNotificationPermissionsResult = React.useCallback(
    (_allowed: unknown) => {
      const globalLog = getGlobalEventsLogs();
      const allowed = Boolean(_allowed);

      logger.debug('sendNotificationPermissionsResult', { _allowed: _allowed as never });

      dispatch(setPushNotificationsAllowed(allowed));

      /**
       * Метод вызывается либо в при инициализации (тогда не нужно отправлять события, т.к еще не создан events subscriber),
       * либо когда открыта модалка и мы ждем, когда пользак включит или не включит пуши (тогда событие нужно отправлять)
       * */
      if (isModalOpened) {
        if (allowed) {
          globalLog.produce(PUSH_TOPIC, { type: PUSH_ALLOW, value: true });
        } else {
          globalLog.produce(PUSH_TOPIC, { type: PUSH_SKIP, value: true });
        }
      }
    },
    [dispatch, isModalOpened, logger],
  );

  /** Инициализирует метод в window для вызова в аппках через бридж. Аппки передают через него факт включения/пропуска настройки пушей */
  React.useEffect(() => {
    window.appInterfaceV2 = window.appInterfaceV2 || {};
    window.appInterfaceV2.sendNotificationPermissionsResult = sendNotificationPermissionsResult;

    return () => {
      if (window.appInterfaceV2) {
        delete window.appInterfaceV2.sendNotificationPermissionsResult;
      }
    };
  }, [sendNotificationPermissionsResult]);

  return (
    <RequestPushSettings
      open={isModalOpened}
      onOpenSettingsClick={handleOpenSettingsClick}
      onSkipClick={handleSkipSettingsClick}
    />
  );
};
